import { useState, useRef, useCallback, useEffect } from 'react'
import { Mp3Encoder } from '@breezystack/lamejs'

export type RecordFormat = 'webm' | 'wav' | 'mp3'

interface AudioRecorderHook {
  isRecording: boolean
  startRecording: () => Promise<void>
  stopRecording: () => void
  isSupported: boolean
}

// An active recording, whatever the format. stop() saves the file.
interface RecordingSession {
  stop: () => void
}

const MP3_KBPS = 256
const MP3_SAMPLE_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000]

// Preferred MediaRecorder containers, best first. Chrome/Firefox take webm/ogg, Safari takes mp4.
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
]

const pickMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return ''
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || ''
}

const extensionFor = (mimeType: string) => {
  if (mimeType.startsWith('audio/mp4')) return 'm4a'
  if (mimeType.startsWith('audio/ogg')) return 'ogg'
  return 'webm'
}

const timestamp = () => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-` +
    `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  )
}

const downloadBlob = (blob: Blob, extension: string) => {
  if (blob.size === 0) return
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `coolwaves-${timestamp()}.${extension}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// Collects stereo PCM in 4096-frame blocks and posts them to the main thread.
// A 'flush' message posts the partial block, then 'done'.
const PCM_CAPTURE_WORKLET = `
class PcmCapture extends AudioWorkletProcessor {
  constructor() {
    super()
    this.size = 4096
    this.reset()
    this.port.onmessage = (e) => {
      if (e.data === 'flush') {
        if (this.n > 0) this.port.postMessage([this.l.slice(0, this.n), this.r.slice(0, this.n)])
        this.reset()
        this.port.postMessage('done')
      }
    }
  }
  reset() {
    this.l = new Float32Array(this.size)
    this.r = new Float32Array(this.size)
    this.n = 0
  }
  process(inputs) {
    const input = inputs[0]
    if (input && input.length) {
      this.l.set(input[0], this.n)
      this.r.set(input[1] || input[0], this.n)
      this.n += input[0].length
      if (this.n >= this.size) {
        this.port.postMessage([this.l, this.r], [this.l.buffer, this.r.buffer])
        this.reset()
      }
    }
    return true
  }
}
registerProcessor('pcm-capture', PcmCapture)
`

let workletUrl: string | null = null
const getWorkletUrl = () => {
  if (!workletUrl) {
    workletUrl = URL.createObjectURL(
      new Blob([PCM_CAPTURE_WORKLET], { type: 'application/javascript' })
    )
  }
  return workletUrl
}

const floatTo16 = (input: Float32Array) => {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out
}

// Receives stereo PCM blocks and produces the final file
interface PcmSink {
  write: (left: Float32Array, right: Float32Array) => void
  finish: () => Blob
}

const createWavSink = (sampleRate: number): PcmSink => {
  const chunks: BlobPart[] = []
  let frames = 0

  return {
    write(left, right) {
      const l = floatTo16(left)
      const r = floatTo16(right)
      const interleaved = new Int16Array(l.length * 2)
      for (let i = 0; i < l.length; i++) {
        interleaved[i * 2] = l[i]
        interleaved[i * 2 + 1] = r[i]
      }
      chunks.push(interleaved)
      frames += l.length
    },
    finish() {
      const channels = 2
      const bytesPerSample = 2
      const dataSize = frames * channels * bytesPerSample
      const header = new DataView(new ArrayBuffer(44))
      const writeString = (offset: number, str: string) => {
        for (let i = 0; i < str.length; i++) {
          header.setUint8(offset + i, str.charCodeAt(i))
        }
      }
      writeString(0, 'RIFF')
      header.setUint32(4, 36 + dataSize, true)
      writeString(8, 'WAVE')
      writeString(12, 'fmt ')
      header.setUint32(16, 16, true) // PCM chunk size
      header.setUint16(20, 1, true) // PCM format
      header.setUint16(22, channels, true)
      header.setUint32(24, sampleRate, true)
      header.setUint32(28, sampleRate * channels * bytesPerSample, true)
      header.setUint16(32, channels * bytesPerSample, true)
      header.setUint16(34, bytesPerSample * 8, true)
      writeString(36, 'data')
      header.setUint32(40, dataSize, true)
      return new Blob([header.buffer, ...chunks], { type: 'audio/wav' })
    },
  }
}

// Encodes as it goes so long recordings don't sit in memory as raw PCM
const createMp3Sink = (sampleRate: number): PcmSink => {
  const encoder = new Mp3Encoder(2, sampleRate, MP3_KBPS)
  const chunks: BlobPart[] = []

  return {
    write(left, right) {
      const out = encoder.encodeBuffer(floatTo16(left), floatTo16(right))
      if (out.length > 0) chunks.push(new Uint8Array(out))
    },
    finish() {
      const out = encoder.flush()
      if (out.length > 0) chunks.push(new Uint8Array(out))
      return new Blob(chunks, { type: 'audio/mpeg' })
    },
  }
}

const startMediaRecorder = (
  stream: MediaStream,
  onDone: () => void
): RecordingSession => {
  const mimeType = pickMimeType()
  const recorder = new MediaRecorder(stream, {
    ...(mimeType && { mimeType }),
    audioBitsPerSecond: 256000,
  })
  const chunks: Blob[] = []

  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data)
  }
  recorder.onstop = () => {
    const type = recorder.mimeType || mimeType || 'audio/webm'
    downloadBlob(new Blob(chunks, { type }), extensionFor(type))
    onDone()
  }

  // Emit chunks every second so a long session doesn't sit in one buffer
  recorder.start(1000)
  console.log('⏺ Recording started:', recorder.mimeType)

  return {
    stop: () => {
      if (recorder.state !== 'inactive') recorder.stop()
    },
  }
}

const startPcmRecorder = async (
  stream: MediaStream,
  format: 'wav' | 'mp3',
  onDone: () => void
): Promise<RecordingSession> => {
  let context = new AudioContext()
  // MP3 tops out at 48 kHz; resample if the device runs higher
  if (format === 'mp3' && !MP3_SAMPLE_RATES.includes(context.sampleRate)) {
    await context.close()
    context = new AudioContext({ sampleRate: 48000 })
  }
  await context.audioWorklet.addModule(getWorkletUrl())

  const source = context.createMediaStreamSource(stream)
  // Explicit stereo input upmixes mono devices to both channels
  const capture = new AudioWorkletNode(context, 'pcm-capture', {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    channelCount: 2,
    channelCountMode: 'explicit',
    channelInterpretation: 'speakers',
  })
  // The worklet only runs while connected to the destination; keep it silent
  const mute = context.createGain()
  mute.gain.value = 0
  source.connect(capture)
  capture.connect(mute)
  mute.connect(context.destination)

  const sink =
    format === 'wav'
      ? createWavSink(context.sampleRate)
      : createMp3Sink(context.sampleRate)
  let stopping = false

  capture.port.onmessage = (e) => {
    if (e.data === 'done') {
      source.disconnect()
      capture.disconnect()
      mute.disconnect()
      context.close()
      downloadBlob(sink.finish(), format)
      onDone()
      return
    }
    const [left, right] = e.data as [Float32Array, Float32Array]
    sink.write(left, right)
  }

  console.log(`⏺ Recording started: ${format} @ ${context.sampleRate} Hz`)

  return {
    stop: () => {
      if (stopping) return
      stopping = true
      capture.port.postMessage('flush')
    },
  }
}

// Records the selected input device to a file that is downloaded on stop.
// Uses its own stream so the recording survives the visualizer rebuilding its
// audio graph (mode/theme changes).
export const useAudioRecorder = (
  deviceId: string,
  format: RecordFormat
): AudioRecorderHook => {
  const [isRecording, setIsRecording] = useState(false)
  const sessionRef = useRef<RecordingSession | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const isSupported =
    format === 'webm'
      ? typeof MediaRecorder !== 'undefined'
      : typeof AudioWorkletNode !== 'undefined'

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  const startRecording = useCallback(async () => {
    if (!isSupported || sessionRef.current) return

    const onDone = () => {
      releaseStream()
      sessionRef.current = null
      setIsRecording(false)
    }

    try {
      // Disable voice processing so the recording matches the source signal
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          channelCount: { ideal: 2 },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      })
      streamRef.current = stream

      sessionRef.current =
        format === 'webm'
          ? startMediaRecorder(stream, onDone)
          : await startPcmRecorder(stream, format, onDone)
      setIsRecording(true)
    } catch (error) {
      console.error('Error starting recording:', error)
      onDone()
    }
  }, [deviceId, format, isSupported])

  const stopRecording = useCallback(() => {
    sessionRef.current?.stop()
  }, [])

  // Stop (and save) if the component unmounts mid-recording
  useEffect(() => {
    return () => {
      sessionRef.current?.stop()
    }
  }, [])

  return { isRecording, startRecording, stopRecording, isSupported }
}
